import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { verifyFeatureAccess } from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import { captureAnalyticsOncePerSession } from '../../actions/helpers/sessionAnalytics';
import csrfHeaders from '../../utils/csrfHeaders';
import { formatPhoneNumber } from '../../containers/Voice/helpers';
import ReplyFaxNumberChoice from '../referralReplyFax/ReplyFaxNumberChoice';
import {
  REPLY_FAX_DESTINATION_CHANGED,
  UNCHOSEN_REPLY_CHOICE,
  currentNumber,
  defaultReplyChoice,
  replyChoiceRequest,
  replyFaxDestinationUrl,
} from '../referralReplyFax/replyFaxApi';

import './referralReplyFaxApproval.scss';

export const REFERRAL_REPLY_FAX_APPROVAL_FEATURE = 'referral_reply_fax_approval';

export { replyFaxDestinationUrl };

// A lookup that never answers must not hold Send forever: after this long the
// modal falls back to today's behavior (the server still refuses to send to an
// unapproved number).
export const REPLY_FAX_LOOKUP_TIMEOUT_MS = 8000;

const withTimeout = (promise, ms) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('reply_fax_lookup_timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const APPROVED_SCOPE_NOTES = {
  referral: 'Reply number confirmed for this referral.',
  sender: 'Reply number on the fax, confirmed for this line.',
  sender_default: 'Reply number set up for this line.',
};

// Inline messages for the machine codes the server sends with a 403/422.
const APPROVAL_ERROR_CODES = {
  scope_not_allowed: 'You can\'t save a number for this line. Untick "Use this number for future faxes" or ask your Admin.',
  invalid_scope: 'That option is not available for this referral. Choose another option and try again.',
  no_sender_fax_number: 'This fax has no sender number, so the number can only be used for this referral.',
  invalid_sender: 'This fax has no sender number, so the number can only be used for this referral.',
  blacklisted: 'This number is blocked for your organization and cannot be used. Contact your Admin.',
};

const approvalErrorMessage = (error) => {
  const response = error?.response;
  if (!response) {
    // No answer from the server: the choice may or may not have been saved.
    return 'Could not confirm the reply number. Check your connection and try again.';
  }
  const { status, data } = response;
  const code = data?.error;
  if ((status === 422 || status === 403) && APPROVAL_ERROR_CODES[code]) {
    return APPROVAL_ERROR_CODES[code];
  }
  if (status === 422) {
    return code === 'invalid_number'
      ? 'This number no longer matches the fax. Close and reopen Reply to see the latest numbers.'
      : 'This number cannot be used. It may be invalid or blocked. Choose the other number or contact your Admin.';
  }
  if (status === 403) {
    return 'Choosing reply fax numbers is not available for your organization.';
  }
  return 'Could not save the reply number. The fax number was not changed. Please try again.';
};

// Asks which number a referral's reply goes to when the fax lists a reply
// number nobody has confirmed: the same choice as the viewer's reply number
// dialog. While the choice is pending the parent hides its reply form and this
// renders it in its place.
// Renders nothing unless the org has `referral_reply_fax_approval` AND the
// reply_fax_destination endpoint answers with enabled:true — a missing
// endpoint, any request failure, or enabled:false leaves the reply modal
// exactly as it was once the lookup settles (onLookupSettled).
const ReferralReplyFaxApproval = ({
  sendToken,
  onApprovedDestination,
  onDecisionPendingChange,
  onLookupSettled,
  onApproved,
  onFooterActionChange,
  lookupTimeoutMs,
}) => {
  const [destination, setDestination] = useState(null);
  const [decision, setDecision] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState(UNCHOSEN_REPLY_CHOICE);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  // Keep the latest callbacks without re-running the load effect when the parent re-renders.
  const callbacksRef = useRef({
    onApprovedDestination, onDecisionPendingChange, onLookupSettled, onFooterActionChange,
  });
  useEffect(() => {
    callbacksRef.current = {
      onApprovedDestination, onDecisionPendingChange, onLookupSettled, onFooterActionChange,
    };
  });

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!sendToken) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        // One deadline covers both requests, so Send is never held longer than the limit.
        const deadline = Date.now() + lookupTimeoutMs;
        const hasAccess = await withTimeout(
          verifyFeatureAccess(REFERRAL_REPLY_FAX_APPROVAL_FEATURE),
          lookupTimeoutMs,
        );
        if (cancelled || hasAccess !== true) return;
        const remainingMs = Math.max(0, deadline - Date.now());
        const { data } = await withTimeout(
          axios.get(replyFaxDestinationUrl(sendToken), { timeout: remainingMs || 1 }),
          remainingMs,
        );
        if (cancelled || !data || data.enabled !== true) return;

        setDestination(data);
        setChoice(defaultReplyChoice(data));
        if (data.approval_state === 'pending') {
          callbacksRef.current.onDecisionPendingChange(true);
          captureAnalyticsOncePerSession('referral_reply_fax_approval_prompt_shown', {});
        } else if (data.approval_state === 'approved' && data.resolved_destination) {
          callbacksRef.current.onApprovedDestination(data.resolved_destination, { source: 'lookup' });
        }
      } catch (lookupError) {
        // No endpoint (404), no access, a network error or a timeout: the modal falls back
        // to today's behavior with no prompt, and the server still refuses to
        // send to an unapproved number. Counted once per session so a broken
        // lookup is visible without one event per modal open.
        if (!cancelled) {
          captureAnalyticsOncePerSession('referral_reply_fax_approval_lookup_failed', {
            status: lookupError?.response?.status || null,
          });
        }
      } finally {
        // Send stays blocked until here, so a fast click cannot skip the prompt.
        if (!cancelled) callbacksRef.current.onLookupSettled();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [sendToken, lookupTimeoutMs]);

  // The choice is recorded server-side; it also releases a held automatic
  // reply to the chosen number.
  const submitChoice = async () => {
    const request = replyChoiceRequest(destination, choice);
    if (submitting || !request) return;

    setSubmitting(true);
    setError(null);
    captureAnalytics('referral_reply_fax_approval_decision', { decision: choice.option, scope: request.scope });

    try {
      const { data } = await axios.post(
        replyFaxDestinationUrl(sendToken),
        { scope: request.scope, fax_number: request.faxNumber },
        { headers: csrfHeaders() },
      );
      if (!isMountedRef.current) return;
      if (!data || data.approval_state !== 'approved' || !data.resolved_destination) {
        setError('The reply number was not saved. The fax number was not changed. Please try again.');
        return;
      }
      window.dispatchEvent(new CustomEvent(REPLY_FAX_DESTINATION_CHANGED, { detail: { sendToken } }));
      setDestination(data);
      onApprovedDestination(data.resolved_destination, { source: 'decision' });
      onApproved();
      if (data.released_held_reply === true) {
        // The choice queued this referral's held automatic reply. The reply
        // form stays hidden: nothing on the server stops a manual reply from
        // going out alongside the queued one.
        setDecision('released');
        return;
      }
      setDecision('chosen');
      onDecisionPendingChange(false);
    } catch (requestError) {
      if (!isMountedRef.current) return;
      setError(approvalErrorMessage(requestError));
    } finally {
      if (isMountedRef.current) setSubmitting(false);
    }
  };

  // With no other number to choose, declining clears the block without a
  // server call, so the reply goes to whatever the user enters.
  const declineExtracted = () => {
    if (submitting) return;
    captureAnalytics('referral_reply_fax_approval_decision', { decision: 'declined', scope: null });
    setError(null);
    setDecision('declined');
    onDecisionPendingChange(false);
  };

  // The choice's buttons live in the modal footer beside Cancel: the parent
  // renders whatever this reports, and null while no choice is on screen.
  const actionsRef = useRef({});
  actionsRef.current = { submitChoice, declineExtracted };
  const choosing = destination?.approval_state === 'pending' && !decision;
  const canContinue = Boolean(replyChoiceRequest(destination, choice));
  const canDecline = choosing && !currentNumber(destination);
  useEffect(() => {
    callbacksRef.current.onFooterActionChange(choosing ? {
      name: 'Continue',
      disabled: !canContinue || submitting,
      loading: submitting,
      onClick: () => actionsRef.current.submitChoice(),
      secondary: canDecline
        ? { name: 'Don\'t use this number', onClick: () => actionsRef.current.declineExtracted() }
        : null,
    } : null);
  }, [choosing, canContinue, canDecline, submitting]);

  if (!destination) return null;

  const {
    approval_state: approvalState,
    approved_scope: approvedScope,
    resolved_destination: resolvedDestination,
  } = destination;

  if (decision === 'released') {
    return (
      <div className="referral-reply-fax-approval__note" data-testid="reply-fax-released-note">
        {`The automatic reply for this referral was queued to ${formatPhoneNumber(resolvedDestination)}. You can close this window.`}
      </div>
    );
  }

  if (decision === 'chosen') {
    return (
      <div className="referral-reply-fax-approval__note" data-testid="reply-fax-chosen-note">
        {`Replying to ${formatPhoneNumber(resolvedDestination)}.`}
      </div>
    );
  }

  if (decision === 'declined') {
    return (
      <div className="referral-reply-fax-approval__note" data-testid="reply-fax-declined-note">
        The number on the fax was not used. Enter the fax number to reply to.
      </div>
    );
  }

  if (approvalState === 'approved') {
    const note = APPROVED_SCOPE_NOTES[approvedScope] || 'Reply fax number approved.';
    return (
      <div className="referral-reply-fax-approval__note" data-testid="reply-fax-approved-note">
        {note}
      </div>
    );
  }

  if (approvalState !== 'pending') return null;

  return (
    <div className="referral-reply-fax-approval" data-testid="reply-fax-approval-prompt">
      <p className="referral-reply-fax-approval__lead">
        Which number should this reply go to?
        {destination.automatic_reply?.reply_waiting && ' An automatic reply is waiting and sends to the number you choose.'}
      </p>
      <ReplyFaxNumberChoice
        destination={destination}
        sendToken={sendToken}
        value={choice}
        onChange={setChoice}
        disabled={submitting}
      />
      {error && (
        <div className="referral-reply-fax-approval__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

ReferralReplyFaxApproval.propTypes = {
  sendToken: PropTypes.string,
  onApprovedDestination: PropTypes.func.isRequired,
  onDecisionPendingChange: PropTypes.func,
  onLookupSettled: PropTypes.func,
  onApproved: PropTypes.func,
  onFooterActionChange: PropTypes.func,
  lookupTimeoutMs: PropTypes.number,
};

ReferralReplyFaxApproval.defaultProps = {
  sendToken: null,
  onDecisionPendingChange: () => {},
  onLookupSettled: () => {},
  onApproved: () => {},
  onFooterActionChange: () => {},
  lookupTimeoutMs: REPLY_FAX_LOOKUP_TIMEOUT_MS,
};

export default ReferralReplyFaxApproval;
